import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { PushServiceWorkerError, registerPushServiceWorker } from '@/lib/pushServiceWorker';
import { isMedianApp } from '@/lib/medianOneSignal';
import { detectDeviceLabel, detectPlatform } from '@/lib/deviceLabel';

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Inside the native app notifications are handled by OneSignal, so the
    // webview must never register itself as a separate browser device.
    if (isMedianApp()) {
      setIsSupported(false);
      return;
    }
    // Check if push notifications are supported
    setIsSupported('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && window.isSecureContext);
  }, []);

  const requestPermission = async () => {
    if (isMedianApp()) {
      // The native app asks for permission through OneSignal instead.
      return false;
    }

    if (!isSupported) {
      toast.error(window.isSecureContext ? 'Push notifications are not supported on this device' : 'Notifications need a secure connection to work');
      return false;
    }

    if (!user) {
      toast.error('Please log in to enable notifications');
      return false;
    }

    if (Notification.permission === 'denied') {
      toast.error('Notifications are blocked in your browser settings');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        return await subscribeToPush();
      } else {
        toast.error(permission === 'denied' ? 'Notifications are blocked in your browser settings' : 'Notification permission was not enabled');
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to request notification permission');
      return false;
    }
  };

  const saveSubscription = async (subscription: PushSubscription) => {
    if (!user) return false;
    const json = subscription.toJSON();
    const deviceFields = {
      device_label: detectDeviceLabel(),
      platform: detectPlatform(),
    };
    // One row per device (unique on user_id + endpoint), so signing in on a
    // phone no longer replaces the same member's laptop registration.
    const { data: existing } = await supabase
      .from('push_subscriptions')
      .select('id, subscription')
      .eq('user_id', user.id);

    const match = (existing || []).find(
      (row: any) => row?.subscription?.endpoint === json.endpoint,
    );

    const { error } = match
      ? await supabase
          .from('push_subscriptions')
          .update({ subscription: json as any, ...deviceFields } as any)
          .eq('id', (match as any).id)
      : await supabase
          .from('push_subscriptions')
          .insert({ user_id: user.id, subscription: json as any, ...deviceFields } as any);

    if (error) {
      console.error('Error saving push subscription:', error);
      return false;
    }
    return true;
  };


  const subscribeToPush = async (silent = false) => {
    if (!user) return false;


    let createdOrRefreshedSubscription: PushSubscription | null = null;

    try {
      // Get VAPID public key from backend
      const { data, error: vapidError } = await supabase.functions.invoke('get-vapid-key');
      
      if (vapidError) throw vapidError;
      if (!data?.publicKey) throw new Error('No VAPID public key returned');

      const registration = await registerPushServiceWorker();
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);
      
      // Get existing subscription or create a new one
      let subscription = await registration.pushManager.getSubscription();

      if (subscription && !subscriptionMatchesVapidKey(subscription, applicationServerKey)) {
        await subscription.unsubscribe();
        subscription = null;
      }
      
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
        createdOrRefreshedSubscription = subscription;
      }

      // Save subscription to database
      const saved = await saveSubscription(subscription);

      if (!saved) {
        if (createdOrRefreshedSubscription) {
          await createdOrRefreshedSubscription.unsubscribe();
        }
        if (!silent) toast.error('Could not save your notification subscription. Please try again.');
        return false;
      }

      setIsSubscribed(true);
      if (!silent) toast.success('Push notifications enabled!');
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      if (silent) return false;
      if (error instanceof PushServiceWorkerError) {
        toast.error(error.code === 'preview' ? error.message : 'Could not register the notification service worker. Please refresh and try again.');
      } else if (error instanceof DOMException && error.name === 'NotAllowedError') {
        toast.error('Notifications are blocked in your browser settings');
      } else {
        toast.error('Could not enable push notifications. Please refresh and try again.');
      }
      return false;
    }
  };


  const unsubscribe = async () => {
    if (!user) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await (registration as any).pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from database
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', user.id);

      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Failed to disable push notifications');
    }
  };

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user || !isSupported) return;

      try {
        const registration = await registerPushServiceWorker();
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          setIsSubscribed(true);
          // The browser may hold a valid subscription while our database row is
          // missing (row deleted, an earlier save failed, or the user signed in
          // on a device that already had a subscription for another account).
          // Always re-sync so the backend can actually reach this device.
          await saveSubscription(subscription);
        } else {
          setIsSubscribed(false);
          // If the OS still has permission granted but no PushManager
          // subscription exists (typical after installing the site as a PWA
          // on Android — installed app uses a separate storage partition
          // from the browser tab), silently re-subscribe so notifications
          // keep working without forcing the user to re-tap "Enable".
          if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            await subscribeToPush(true);
          }
        }

      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    };

    const timeoutId = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          checkSubscription();
        }, { timeout: 5000 });
      } else {
        checkSubscription();
      }
    }, 8000);

    return () => window.clearTimeout(timeoutId);
  }, [user, isSupported]);

  // Inside the native app: drop only this app's own web-view registration, so
  // one handset is never alerted twice. Registrations from other devices
  // (a tablet, a second phone) are left alone.
  useEffect(() => {
    if (!user || !isMedianApp()) return;

    const cleanup = async () => {
      try {
        let endpoint: string | null = null;

        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();
          const subscription = await registration?.pushManager?.getSubscription();
          if (subscription) {
            endpoint = subscription.endpoint;
            await subscription.unsubscribe();
          }
        }

        if (!endpoint) return;

        const { data: rows } = await supabase
          .from('push_subscriptions')
          .select('id, subscription')
          .eq('user_id', user.id);

        const stale = (rows || []).filter(
          (row: any) => row?.subscription?.endpoint === endpoint,
        );
        for (const row of stale) {
          await supabase.from('push_subscriptions').delete().eq('id', (row as any).id);
        }
      } catch (error) {
        console.error('Error cleaning up webview push subscription:', error);
      }
    };

    const timeoutId = window.setTimeout(cleanup, 4000);
    return () => window.clearTimeout(timeoutId);
  }, [user]);



  return {
    isSupported,
    isSubscribed,
    requestPermission,
    unsubscribe
  };
};

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function subscriptionMatchesVapidKey(subscription: PushSubscription, applicationServerKey: Uint8Array) {
  const existingKey = subscription.options.applicationServerKey;
  if (!existingKey) return false;

  const existingBytes = new Uint8Array(existingKey);
  if (existingBytes.length !== applicationServerKey.length) return false;

  return existingBytes.every((byte, index) => byte === applicationServerKey[index]);
}
